import React, { useContext } from 'react'
import { Experiment } from '@deal/experiment-js'
import { IdentityConsumer, IdentityContext } from '#src/containers/Identity'
import { EngagementChannelCategoryAssetsComponent, DepartmentType } from '#src/generated/graphql'
import { setRegistrationDepartment, setLandingPageKey } from '#src/services/registrationValues'
import BrandCategoryModal from './BrandCategoryModal'
import CategoryModal from './CategoryModal'
import CategoryHeader from './CategoryHeader'
import EmbeddedLandingPage from './EmbeddedLandingPage'
import EngagementModal from './EngagementModal'
import FixedBar from './FixedBar'
import FullPageTakeover from './FullPageTakeover'
import SMSModal from './SMSModal'

type ExperimentVariation =
  | 'category-header'
  | 'brand-category-modal'
  | 'category-modal-dual-headline'
  | 'category-modal-expert-count'
  | 'embedded-landing-page'
  | 'engagement-modal'
  | 'full-page-takeover'
  | 'fixed-bar'
  | 'banner'
  | 'sms-modal'

export type ExperimentPlacements = 'product' | 'search' | 'experience'

type DefaultExperiment = {
  placement: ExperimentPlacements
  variations: ExperimentVariation[]
}

interface EngagementChannelHandlerProps {
  categoryId: string
  sellableId?: string
  defaultExperiment: DefaultExperiment
}

interface ExperimentWrapperProps {
  allowLoggedIn?: boolean
  landingPageKey: string
  departmentSlug?: string
}

const ExperimentWrapper: React.SFC<ExperimentWrapperProps> = ({
  children,
  allowLoggedIn,
  landingPageKey,
  departmentSlug
}) => {
  return (
    <IdentityConsumer>
      {({ myself }) => {
        if (myself && !allowLoggedIn) {
          return null
        }

        // We would only ever set this for guest users
        if (!myself) {
          /**
           * Set the landing page key so we can directly target a specific
           * creative on the campaign side
           */
          setLandingPageKey(landingPageKey)

          /**
           * Set the registration department for user, this can be
           * overriden in the event they navigate to another page
           * and register there.
           */
          setRegistrationDepartment(departmentSlug, true)
        }

        if (children && typeof children === 'function') {
          return children()
        } else {
          return children
        }
      }}
    </IdentityConsumer>
  )
}

const EngagementChannelHandler: React.SFC<EngagementChannelHandlerProps> = ({
  defaultExperiment: { variations, placement },
  categoryId,
  sellableId
}) => {
  const { myself } = useContext(IdentityContext)

  return (
    <EngagementChannelCategoryAssetsComponent
      variables={{
        categoryId: categoryId
      }}
    >
      {({ data }) => {
        if (data && data.category) {
          /**
           * We can't launch any of the engagement channel treatments if the department isn't launched or accepting new requests
           */
          if (!data.category.department?.acceptingRequests || !data.category.department.launched) {
            return null
          }

          return (
            <Experiment
              id={`engagement-channel-${placement}`}
              default="full-page-takeover"
              options={{
                suppressTracking: !!myself
              }}
            >
              {({ result }) => {
                if (result && variations.includes(result as ExperimentVariation)) {
                  const landingPageKey = `${result}-${data.category.slug}-${placement}`
                  const maybeDepartmentSlug = data.category.department?.slug

                  return (
                    <ExperimentWrapper
                      allowLoggedIn={result === 'sms-modal'}
                      landingPageKey={landingPageKey}
                      departmentSlug={maybeDepartmentSlug}
                    >
                      {() => {
                        switch (result) {
                          case 'category-header':
                            /**
                             * This is currently for golf only, handled on the search placement but also checked here
                             */
                            if (data.category.department?.type !== DepartmentType.GOLF) {
                              return null
                            }

                            return <CategoryHeader categorySlug={data.category.slug} />

                          case 'sms-modal':
                            return (
                              <SMSModal categoryId={data.category.id} sellableId={sellableId} />
                            )

                          case 'engagement-modal':
                            return (
                              <EngagementModal category={data.category} placement={placement} />
                            )

                          case 'brand-category-modal':
                            return <BrandCategoryModal sellableId={sellableId} />

                          case 'embedded-landing-page':
                            return <EmbeddedLandingPage sellableId={sellableId} />

                          case 'category-modal-dual-headline':
                            return (
                              <CategoryModal
                                categorySlug={data.category.slug}
                                variation="v1"
                                sellableId={sellableId}
                              />
                            )

                          case 'category-modal-expert-count':
                            return (
                              <CategoryModal
                                categorySlug={data.category.slug}
                                variation="v2"
                                sellableId={sellableId}
                              />
                            )

                          case 'full-page-takeover':
                            return (
                              <FullPageTakeover category={data.category} placement={placement} />
                            )

                          case 'fixed-bar':
                            return <FixedBar category={data.category} />
                        }
                      }}
                    </ExperimentWrapper>
                  )
                }

                return null
              }}
            </Experiment>
          )
        } else {
          return null
        }
      }}
    </EngagementChannelCategoryAssetsComponent>
  )
}

export default EngagementChannelHandler
